import React, { FocusEvent, SyntheticEvent, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { defaultColors } from 'utils/colors';
import { ColorButton } from 'components/ColorButton';
import { ColorField } from 'components/ColorField';
import { CustomizationPreview } from '../CustomizationPreview';
import { useCustomStyles } from '../providers/StylesProvider';
import {
  Button,
  ContainerColors,
  Flex,
  Form,
  FormField,
  FormInfo,
  FormLabel,
  IconButton,
  Layout,
  Preview,
  TextField,
  Title,
} from './customization-form.styles';
import { useMediaQuery } from 'react-responsive';
import { CheckboxField } from 'components/CheckboxField';
import { UploadFile } from 'components/UploadFile';
import { customizationApi } from 'api/customizationApi';
import { StylePayload } from 'api/types';
import { toastFormConfig, toastGeneralConfig } from 'utils/toast';
import { urlRegex } from 'utils/url-regex';
import { ModalSuccess } from './ModalSuccess';
import { redirectUserToPdPDashboard } from 'utils/auth-token';
import { BsArrowLeft } from 'react-icons/bs';
import { invalidNames } from 'utils/invalid-names';
export const CustomizationForm = () => {
  const { styles, setStyles } = useCustomStyles();
  const [bgColors, setBgColors] = useState(defaultColors);
  const [fgColors, setFgColors] = useState(defaultColors);
  const [isLoading, setIsLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [isFetching, setIsFetching] = useState(false);

  const isTabletOrMobile = useMediaQuery({ query: '(max-width: 992px)' });

  useEffect(() => {
    customizationApi
      .get(`partner/partnerLabellessStyle`)
      .then((res: any) => {
        const backgroundColor = res?.data?.backgroundColorsOption.toLowerCase();
        const primaryColor = res?.data?.inputsColorsOption.toLowerCase();
        if (backgroundColor) {
          if (!bgColors.includes(backgroundColor)) {
            setBgColors((prev) => [...prev, backgroundColor]);
          }
        }
        if (primaryColor) {
          if (!fgColors.includes(primaryColor)) {
            setFgColors((prev) => [...prev, primaryColor]);
          }
        }
        setStyles({
          file: res?.data?.logo ?? '',
          brandName: res?.data?.shortName ?? '',
          name: res?.data?.pageName ?? '',
          backgroundColor: backgroundColor ?? defaultColors[0],
          primaryColor: primaryColor ?? defaultColors[1],
          hideLogo: res?.data?.hideCredihomeLogo ?? false,
        });
      })
      .catch((error) => {
        if (error?.response?.status === 404) {
          toast.error(`Parceria não encontrada`, toastGeneralConfig);
        } else {
          toast.error(`Erro ao obter dados da parceria.`, toastGeneralConfig);
        }
      })
      .finally(() => setIsFetching(false));
  }, []);

  const onChangeStyle = (value: any) => {
    setStyles({ ...styles, ...value });
  };

  const onBackgroundColorBlur = (e: FocusEvent<HTMLInputElement>) => {
    const newColor = e.target.value.toLowerCase();
    if (!bgColors.includes(newColor)) {
      setBgColors((prev) => [...prev, newColor]);
    }
  };

  const onPrimaryColorBlur = (e: FocusEvent<HTMLInputElement>) => {
    const newColor = e.target.value.toLowerCase();
    if (!fgColors.includes(newColor)) {
      setFgColors((prev) => [...prev, newColor]);
    }
  };

  const submit = (e: SyntheticEvent) => {
    e.preventDefault();
    if (!urlRegex.test(styles.name)) {
      toast.error(
        'URL do simulador inválida. Use somente letras, números ou hífens, sem acentuação e sem caracteres especiais.',
        toastFormConfig,
      );
      return;
    }
    if (invalidNames.includes(styles.name)) {
      toast.error(
        'Essa URL do simulador não está disponível. Por favor escolha outra opção e tente novamente.',
        toastFormConfig,
      );
      return;
    }
    const payload: StylePayload = {
      shortName: styles.brandName,
      pageName: styles.name,
      backgroundColorsOption: styles.backgroundColor,
      inputsColorsOption: styles.primaryColor,
      hideCredihomeLogo: styles.hideLogo,
    };
    if (styles.file) {
      payload.logo = styles.file;
    }
    setIsLoading(true);
    customizationApi
      .post('/partnerLabellessStyle', payload)
      .then(() => {
        setOpenModal(true);
      })
      .catch((error) => {
        if (error?.response?.status === 409) {
          toast.error(
            'A URL do simulador escolhida já está em uso. Por favor, escolha outra.',
            toastFormConfig,
          );
        } else {
          toast.error('Erro ao publicar simulador.', toastFormConfig);
        }
      })
      .finally(() => setIsLoading(false));
  };

  return (
    <Layout>
      <Form onSubmit={submit}>
        <Flex>
          <IconButton>
            <BsArrowLeft
              style={{ marginBottom: -4 }}
              size="24"
              onClick={redirectUserToPdPDashboard}
            />
          </IconButton>
          <Title>Personalize seu simulador</Title>
        </Flex>
        <FormField>
          <FormLabel htmlFor="logoFile">Logo</FormLabel>
          <UploadFile onChange={onChangeStyle} hasFile={Boolean(styles.file)} />
        </FormField>
        <FormField>
          <CheckboxField
            checkboxId="hideLogo"
            label="Ocultar logo da CrediHome"
            checked={styles.hideLogo}
            onChange={(e) => onChangeStyle({ hideLogo: e.target.checked })}
            disabled={isLoading || isFetching}
          />
        </FormField>
        <FormField>
          <FormLabel htmlFor="brandName">Nome da empresa</FormLabel>
          <TextField
            id="brandName"
            type="text"
            value={styles.brandName}
            onChange={(e) => onChangeStyle({ brandName: e.target.value })}
            disabled={isLoading || isFetching}
            required
          />
        </FormField>
        <FormField>
          <FormLabel htmlFor="name">URL do simulador</FormLabel>
          <TextField
            id="name"
            type="text"
            value={styles.name}
            onChange={(e) => onChangeStyle({ name: e.target.value })}
            disabled={isLoading || isFetching}
            required
          />
          <FormInfo>
            {process.env.REACT_APP_SIMULATOR_URL}/<strong>{styles.name}</strong>
          </FormInfo>
        </FormField>
        <FormField>
          <FormLabel htmlFor="backgroundColor">Cor do fundo</FormLabel>
          <ContainerColors>
            {bgColors.map((color: string) => (
              <ColorButton
                key={color}
                color={color}
                isSelected={color === styles.backgroundColor}
                onClick={() => onChangeStyle({ backgroundColor: color })}
                disabled={isLoading || isFetching}
              />
            ))}
            <ColorField
              id="backgroundColor"
              type="color"
              value={styles.backgroundColor}
              onChange={(e) => onChangeStyle({ backgroundColor: e.target.value })}
              onBlur={(e) => onBackgroundColorBlur(e)}
              disabled={isLoading || isFetching}
            />
          </ContainerColors>
        </FormField>
        <FormField>
          <FormLabel htmlFor="primaryColor">Cor de destaque</FormLabel>
          <ContainerColors>
            {fgColors.map((color: string) => (
              <ColorButton
                key={color}
                color={color}
                isSelected={color === styles.primaryColor}
                onClick={() => onChangeStyle({ primaryColor: color })}
                disabled={isLoading || isFetching}
              />
            ))}
            <ColorField
              id="primaryColor"
              type="color"
              value={styles.primaryColor}
              onChange={(e) => onChangeStyle({ primaryColor: e.target.value })}
              onBlur={(e) => onPrimaryColorBlur(e)}
              disabled={isLoading || isFetching}
            />
          </ContainerColors>
        </FormField>
        <Button type="submit" disabled={isLoading || isFetching}>
          {isLoading ? 'Publicando simulador...' : 'Publicar simulador'}
        </Button>
      </Form>
      {!isTabletOrMobile && (
        <Preview>
          <CustomizationPreview />
        </Preview>
      )}

      <ModalSuccess
        open={openModal}
        setOpen={setOpenModal}
        link={`https://${process.env.REACT_APP_SIMULATOR_URL}/${styles.name}`}
      />
    </Layout>
  );
};

CustomizationForm.displayName = 'CustomizationForm';
